import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { LoadingState } from '../ui/loading-state';
import { Breadcrumbs } from '../ui/breadcrumbs';
import { ChevronLeft, ChevronRight, Filter, ChevronDown, ChevronUp, Clock, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { useModel, useModelEarnings } from '../../lib/supabase/models/supabaseModelService';
import { useShifts } from '../../lib/supabase/shifts/supabaseShiftService';
import { formatCurrency } from '../../utils/format';
import { getMonthBoundary } from '../../lib/utils';
import { calculateModelStats } from '../../utils/statsCalculator';
import { OT_SHIFT_CODES_KEYS } from '@shiftly/shared/src/shiftCodes';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';

type GroupByOption = 'day' | 'week' | 'none';
type OTFilter = 'all' | 'ot' | 'no-ot';

interface GroupedShift {
  key: string;
  label: string;
  shifts: any[];
  totalPPV: number;
  totalNonPPV: number;
  totalLivestream: number;
  ppvOnly: number;
  hoursWorked: number;
  averagePerHour: number;
  bestShift: any;
}

const getShiftCodeColor = (shift_code: string) => {
  const isOT = OT_SHIFT_CODES_KEYS.includes(shift_code);
  
  if (isOT) {
    return 'bg-brand-gold text-brand-dark dark:bg-brand-dark dark:text-brand-gold border-brand-gold dark:border-brand-border';
  }
  
  // Different colors for different shift types
  switch (shift_code) {
    case 'REG':
      return 'bg-brand-gold text-brand-dark dark:bg-brand-dark dark:text-brand-gold border-brand-gold dark:border-brand-border';
    case 'WEEKEND':
      return 'bg-brand-pink text-brand-dark dark:bg-brand-dark dark:text-brand-pink border-brand-pink dark:border-brand-border';
    case 'HOLIDAY':
      return 'bg-brand-red text-brand-dark dark:bg-brand-dark dark:text-brand-red border-brand-red dark:border-brand-red';
    default:
      return 'bg-brand-dark text-brand-dark dark:bg-brand-dark dark:text-muted-foreground border-brand-border dark:border-brand-border';
  }
};

const ModelEarningsBreakdownView = () => {
  const { model_id } = useParams<{ model_id: string }>();
  const [selectedMonth, setSelectedMonth] = useState(DateTime.utc());
  const [groupBy, setGroupBy] = useState<GroupByOption>('week');
  const [otFilter, setOtFilter] = useState<OTFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isProjectionExpanded, setIsProjectionExpanded] = useState(false);
  const itemsPerPage = 10;
  
  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [groupBy, otFilter]);
  
  const { data: model, isLoading: modelLoading } = useModel(model_id || '');
  const { data: modelEarnings, isLoading: modelEarningsLoading } = useModelEarnings(model_id || '');
  
  const monthBoundary = getMonthBoundary(selectedMonth);
  
  const filters = useMemo(() => [
    { field: 'model_id', operator: 'eq' as const, value: model_id || '' },
    { field: 'start_date_time', operator: 'gte' as const, value: monthBoundary.start.toISO() },
    { field: 'start_date_time', operator: 'lte' as const, value: monthBoundary.end.toISO() }
  ], [model_id, monthBoundary]);

  const { data: shifts = [], isLoading: shiftsLoading } = useShifts({ filters });

  const submittedShifts = useMemo(() => 
    shifts.filter(shift => shift.submitted), 
    [shifts]
  );

  // Filter shifts by OT status
  const filteredShifts = useMemo(() => {
    if (otFilter === 'all') return submittedShifts;
    return submittedShifts.filter(shift => {
      const isOT = OT_SHIFT_CODES_KEYS.includes(shift.shift_code);
      return otFilter === 'ot' ? isOT : !isOT;
    });
  }, [submittedShifts, otFilter]);

  // Group shifts based on selected grouping
  const groupedShifts = useMemo(() => {
    if (groupBy === 'none') {
      const allShifts = filteredShifts;
      const bestShift = allShifts.reduce((best, current) => {
        const currentPPVOnly = (current.ppv_earned || 0) - (current.noppv_earned || 0);
        const bestPPVOnly = (best.ppv_earned || 0) - (best.noppv_earned || 0);
        return currentPPVOnly > bestPPVOnly ? current : best;
      }, allShifts[0]);
      
      return [{
        key: 'all',
        label: 'All Shifts',
        shifts: allShifts.sort((a, b) => b.start_date_time.toMillis() - a.start_date_time.toMillis()),
        totalPPV: allShifts.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0),
        totalNonPPV: allShifts.reduce((sum, shift) => sum + (shift.noppv_earned || 0), 0),
        totalLivestream: allShifts.reduce((sum, shift) => sum + (shift.livestream_earned || 0), 0),
        ppvOnly: allShifts.reduce((sum, shift) => sum + (shift.ppv_earned || 0) - (shift.noppv_earned || 0), 0),
        hoursWorked: allShifts.reduce((sum, shift) => {
          const duration = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;
          return sum + duration;
        }, 0),
        averagePerHour: 0,
        bestShift
      }];
    }

    const groups: Record<string, GroupedShift> = {};

    filteredShifts.forEach(shift => {
      let key: string;
      let label: string;

      if (groupBy === 'week') {
        const weekStart = shift.start_date_time.startOf('week');
        key = weekStart.toISO() || '';
        label = `Week of ${weekStart.toFormat('MMM dd')}`;
      } else { // day
        key = shift.start_date_time.toFormat('yyyy-MM-dd');
        label = shift.start_date_time.toFormat('MMM dd, yyyy');
      }

      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          shifts: [],
          totalPPV: 0,
          totalNonPPV: 0,
          totalLivestream: 0,
          ppvOnly: 0,
          hoursWorked: 0,
          averagePerHour: 0,
          bestShift: null
        };
      }

      const duration = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;

      groups[key].shifts.push(shift);
      groups[key].totalPPV += shift.ppv_earned || 0;
      groups[key].totalNonPPV += shift.noppv_earned || 0;
      groups[key].totalLivestream += shift.livestream_earned || 0;
      groups[key].ppvOnly += (shift.ppv_earned || 0) - (shift.noppv_earned || 0);
      groups[key].hoursWorked += duration;
    });

    // Calculate average per hour and find best/worst shifts for each group
    Object.values(groups).forEach(group => {
      group.averagePerHour = group.hoursWorked > 0 ? group.totalPPV / group.hoursWorked : 0;

      group.shifts.sort((a, b) => b.start_date_time.toMillis() - a.start_date_time.toMillis());

      if (group.shifts.length > 0) {
        group.bestShift = group.shifts.reduce((best, current) => {
          const currentPPVOnly = (current.ppv_earned || 0) - (current.noppv_earned || 0);
          const bestPPVOnly = (best.ppv_earned || 0) - (best.noppv_earned || 0);
          return currentPPVOnly > bestPPVOnly ? current : best;
        }, group.shifts[0]);
      }
    });

    return Object.values(groups).sort((a, b) => {
      if (groupBy === 'week') {
        return DateTime.fromISO(b.key, { zone: 'UTC' }).toMillis() - DateTime.fromISO(a.key, { zone: 'UTC' }).toMillis();
      } else {
        return b.key.localeCompare(a.key);
      }
    });
  }, [filteredShifts, groupBy]);

  // Ensure current page doesn't exceed total pages when data changes
  useEffect(() => {
    const totalPages = Math.ceil(groupedShifts.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [groupedShifts.length, currentPage, itemsPerPage]);

  // Pagination logic
  const totalPages = Math.ceil(groupedShifts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGroups = groupedShifts.slice(startIndex, endIndex);

  const modelStats = useMemo(() => 
    calculateModelStats(submittedShifts), 
    [submittedShifts]
  );

  const currentMonthEarnings = useMemo(() => {
    const year = selectedMonth.year;
    const month = selectedMonth.month;
    const monthData = modelEarnings?.find((earning: any) => {
      const earningDate = DateTime.fromISO(earning.month, { zone: 'UTC' });
      return earningDate.year === year && earningDate.month === month;
    });
    return monthData?.earnings || 0;
  }, [modelEarnings, selectedMonth]);

  const lastMonthEarnings = useMemo(() => {
    const lastMonth = selectedMonth.minus({ months: 1 });
    const year = lastMonth.year;
    const month = lastMonth.month;
    const monthData = modelEarnings?.find((earning: any) => {
      const earningDate = DateTime.fromISO(earning.month, { zone: 'UTC' });
      return earningDate.year === year && earningDate.month === month;
    });
    return monthData?.earnings || 0;
  }, [modelEarnings, selectedMonth]);

  const additionalStats = useMemo(() => {
    if (submittedShifts.length === 0) return null;

    const totalHours = submittedShifts.reduce((sum, shift) => {
      const duration = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;
      return sum + duration;
    }, 0);
    
    const bestShift = submittedShifts.reduce((best, current) => {
      const currentPPV = current.ppv_earned || 0;
      const bestPPV = best.ppv_earned || 0;
      return currentPPV > bestPPV ? current : best;
    }, submittedShifts[0]);
    
    const otShifts = submittedShifts.filter(shift => OT_SHIFT_CODES_KEYS.includes(shift.shift_code));
    const regularShifts = submittedShifts.filter(shift => !OT_SHIFT_CODES_KEYS.includes(shift.shift_code));
    
    return {
      totalHours,
      bestShift,
      otShifts: otShifts.length,
      regularShifts: regularShifts.length,
      otPercentage: (otShifts.length / submittedShifts.length) * 100
    };
  }, [submittedShifts]);

  const projectionStats = useMemo(() => {
    if (!additionalStats) return null;

    const daysInMonth = selectedMonth.daysInMonth;
    const now = DateTime.utc();
    const currentMonth = now.startOf('month');
    const selectedMonthStart = selectedMonth.startOf('month');

    const isPastMonth = selectedMonthStart < currentMonth;
    const isCurrentMonth = selectedMonthStart.hasSame(currentMonth, 'month');
    const isFutureMonth = selectedMonthStart > currentMonth;
    
    let daysElapsed: number;
    let daysRemaining: number;
    let progressPercentage: number;

    if (isPastMonth) {
      daysElapsed = daysInMonth;
      daysRemaining = 0;
      progressPercentage = 100;
    } else if (isCurrentMonth) {
      const currentDay = now.day;
      daysElapsed = Math.min(currentDay, daysInMonth);
      daysRemaining = daysInMonth - daysElapsed;
      progressPercentage = (daysElapsed / daysInMonth) * 100;
    } else {
      daysElapsed = 0;
      daysRemaining = daysInMonth;
      progressPercentage = 0;
    }
    
    const dailyAverage = daysElapsed > 0 ? currentMonthEarnings / daysElapsed : 0;
    const projectedEarnings = dailyAverage * daysInMonth;
    const targetDailyEarnings = lastMonthEarnings / daysInMonth;
    const targetRemainingEarnings = targetDailyEarnings * daysRemaining;
    const totalNeededForTarget = currentMonthEarnings + targetRemainingEarnings;
    const isOnTrack = projectedEarnings >= lastMonthEarnings;
    const variance = projectedEarnings - lastMonthEarnings;
    const variancePercentage = lastMonthEarnings > 0 ? (variance / lastMonthEarnings) * 100 : 0;
    
    return {
      daysInMonth,
      daysElapsed,
      daysRemaining,
      dailyAverage,
      projectedEarnings,
      progressPercentage,
      targetDailyEarnings,
      totalNeededForTarget,
      isOnTrack,
      variance,
      variancePercentage,
      isPastMonth,
      isCurrentMonth,
      isFutureMonth
    };
  }, [currentMonthEarnings, lastMonthEarnings, selectedMonth, additionalStats]);

  const handlePreviousMonth = () => {
    setSelectedMonth(prev => prev.minus({ months: 1 }));
    setCurrentPage(1);
    setExpandedGroups(new Set());
  };

  const handleNextMonth = () => {
    setSelectedMonth(prev => prev.plus({ months: 1 }));
    setCurrentPage(1);
    setExpandedGroups(new Set());
  };

  const toggleGroupExpansion = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  if (modelLoading || modelEarningsLoading) {
    return <LoadingState />;
  }

  if (!model) {
    return (
      <div className="p-4 text-brand-red bg-brand-red rounded-lg">
        Model not found
      </div>
    );
  }

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Models', href: '/models' },
    { title: model.name, href: `/models/${model.id}` },
    { title: 'Earnings Breakdown' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-1.5 pb-4 border-b">
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
                {model.name} - Earnings Breakdown
              </h1>
              <p className="text-muted-foreground dark:text-muted-foreground">
                Detailed earnings analysis and statistics
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="relative">
        <div className="absolute left-0 inset-y-0 flex items-center">
          <Button variant="ghost" size="icon" onClick={handlePreviousMonth} className="h-full w-8 rounded-xl">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="absolute right-0 inset-y-0 flex items-center">
          <Button variant="ghost" size="icon" onClick={handleNextMonth} className="h-full w-8 rounded-xl">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{selectedMonth.toFormat('MMMM yyyy')} Earnings Summary</CardTitle>
                <CardDescription>
                  Monthly earnings breakdown ({monthBoundary.start.toFormat('MMM dd')} 6:00 AM - {monthBoundary.end.toFormat('MMM dd')} 6:00 AM)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brand-green dark:text-brand-green">
              {formatCurrency(currentMonthEarnings)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total for {selectedMonth.toFormat('MMMM yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Shifts Completed</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brand-gold dark:text-brand-gold">
              {submittedShifts.length}
            </div>
            <p className="text-xs text-muted-foreground">
              {additionalStats && `${additionalStats.regularShifts} Regular, ${additionalStats.otShifts} OT`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hours Worked</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brand-pink dark:text-brand-pink">
              {additionalStats ? additionalStats.totalHours.toFixed(1) : '0'}h
            </div>
            <p className="text-xs text-muted-foreground">
            Total for {selectedMonth.toFormat('MMMM yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PPV Efficiency</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {modelStats.totalNonPPV > 0 ? 
                (modelStats.totalPPV > 0 ? 
                  `${(((modelStats.totalPPV - modelStats.totalNonPPV) / modelStats.totalPPV) * 100).toFixed(1)}%` : 
                  '0%'
                ) : 
                '-'
              }
            </div>
            <p className="text-xs text-muted-foreground">
              PPV Only / Total PPV
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Best Shift</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {additionalStats ? formatCurrency(additionalStats.bestShift.ppv_earned || 0) : '$0'}
            </div>
            <p className="text-xs text-muted-foreground">
              {additionalStats && additionalStats.bestShift.start_date_time.toFormat('MMM dd')} - {additionalStats?.bestShift.shift_code} (PPV)
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Earnings Breakdown</CardTitle>
          <CardDescription>
            Detailed breakdown for {monthBoundary.start.toFormat('d MMMM')} 6:00 AM - {monthBoundary.end.toFormat('d MMMM')} 6:00 AM
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">PPV</p>
              <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                {formatCurrency(modelStats.totalPPV)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Non PPV</p>
              <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                {formatCurrency(modelStats.totalNonPPV)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Average/Hour</p>
              <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                {formatCurrency(modelStats.averageEarningsPerHour)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Average/Shift (8h)</p>
              <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                {formatCurrency(modelStats.averageEarningsPer8HourShift)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Livestream</p>
              <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                {formatCurrency(modelStats.totalLivestream)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {projectionStats && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Monthly Projection & Progress</CardTitle>
                <CardDescription>
                  Comparison with last month and current progress tracking
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsProjectionExpanded(!isProjectionExpanded)}
                className="h-8 w-8 p-0"
              >
                {isProjectionExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {isProjectionExpanded && (
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Month Progress</span>
                    <span>
                      {projectionStats.isPastMonth ? 'Completed' : 
                       projectionStats.isFutureMonth ? 'Not Started' :
                       `${projectionStats.progressPercentage.toFixed(1)}% (${projectionStats.daysElapsed}/${projectionStats.daysInMonth} days)`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-brand-dark dark:bg-brand-dark rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        projectionStats.isPastMonth ? 'bg-brand-green' :
                        projectionStats.isFutureMonth ? 'bg-brand-dark' :
                        'bg-brand-gold'
                      }`}
                      style={{ width: `${Math.min(projectionStats.progressPercentage, 100)}%` }}
                    />
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Month</TableHead>
                      <TableHead>Last Month</TableHead>
                      <TableHead>Projection</TableHead>
                      <TableHead>Variance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Total Earnings</TableCell>
                      <TableCell>{formatCurrency(currentMonthEarnings)}</TableCell>
                      <TableCell>{formatCurrency(lastMonthEarnings)}</TableCell>
                      <TableCell>
                        {projectionStats.isFutureMonth ? '-' : formatCurrency(projectionStats.projectedEarnings)}
                      </TableCell>
                      <TableCell className={projectionStats.variance >= 0 ? 'text-brand-green' : 'text-brand-red'}>
                        {projectionStats.isFutureMonth ? '-' :
                         `${projectionStats.variance >= 0 ? '+' : ''}${formatCurrency(projectionStats.variance)} (${projectionStats.variancePercentage >= 0 ? '+' : ''}${projectionStats.variancePercentage.toFixed(1)}%)`
                        }
                      </TableCell>
                      <TableCell>
                        {projectionStats.isFutureMonth ? (
                          <Badge variant="secondary" className="bg-brand-dark text-brand-dark dark:bg-brand-dark dark:text-muted-foreground">
                            Future Month
                          </Badge>
                        ) : (
                          <Badge variant={projectionStats.isOnTrack ? 'default' : 'destructive'} className={projectionStats.isOnTrack ? 'bg-brand-green text-brand-dark dark:bg-brand-dark dark:text-brand-green' : ''}>
                            {projectionStats.isPastMonth ? 'Completed' : projectionStats.isOnTrack ? 'On Track' : 'Behind'}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Daily Average</TableCell>
                      <TableCell>{formatCurrency(projectionStats.dailyAverage)}</TableCell>
                      <TableCell>{formatCurrency(projectionStats.targetDailyEarnings)}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell className={projectionStats.dailyAverage >= projectionStats.targetDailyEarnings ? 'text-brand-green' : 'text-brand-red'}>
                        {projectionStats.isFutureMonth ? '-' :
                         `${projectionStats.dailyAverage >= projectionStats.targetDailyEarnings ? '+' : ''}${formatCurrency(projectionStats.dailyAverage - projectionStats.targetDailyEarnings)}`
                        }
                      </TableCell>
                      <TableCell>
                        {projectionStats.isFutureMonth ? (
                          <Badge variant="secondary" className="bg-brand-dark text-brand-dark dark:bg-brand-dark dark:text-muted-foreground">
                            N/A
                          </Badge>
                        ) : (
                          <Badge variant={projectionStats.dailyAverage >= projectionStats.targetDailyEarnings ? 'default' : 'destructive'} className={projectionStats.dailyAverage >= projectionStats.targetDailyEarnings ? 'bg-brand-green text-brand-dark dark:bg-brand-dark dark:text-brand-green' : ''}>
                            {projectionStats.isPastMonth ? 'Final' : projectionStats.dailyAverage >= projectionStats.targetDailyEarnings ? 'Above Target' : 'Below Target'}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {projectionStats.isFutureMonth ? (
                    <Card className="bg-brand-dark dark:bg-brand-dark/20 border-brand-border dark:border-brand-border">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-brand-dark dark:text-muted-foreground mb-2">Future Month</h4>
                        <p className="text-sm text-brand-dark dark:text-muted-foreground">
                          No data available for this month yet
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-brand-gold dark:bg-brand-dark/20 border-brand-gold dark:border-brand-border">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-brand-dark dark:text-brand-gold mb-2">
                          {projectionStats.isPastMonth ? 'Final Result vs Last Month' : 'To Match Last Month'}
                        </h4>
                        {projectionStats.isPastMonth ? (
                          <p className="text-sm text-brand-dark dark:text-brand-gold">
                            {projectionStats.variance >= 0 ? 'Exceeded' : 'Fell short of'} last month by {formatCurrency(Math.abs(projectionStats.variance))}
                          </p>
                        ) : (
                          <p className="text-sm text-brand-dark dark:text-brand-gold">
                            Need {formatCurrency(projectionStats.totalNeededForTarget - currentMonthEarnings)} more in {projectionStats.daysRemaining} days
                          </p>
                        )}
                        <p className="text-sm text-brand-gold dark:text-brand-gold mt-1">
                          ({formatCurrency(projectionStats.targetDailyEarnings)} per day {projectionStats.isPastMonth ? 'was needed' : 'needed'})
                        </p>
                      </CardContent>
                    </Card>
                  )}
                  
                  {projectionStats.isFutureMonth ? (
                    <Card className="bg-brand-dark dark:bg-brand-dark/20 border-brand-border dark:border-brand-border">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-brand-dark dark:text-muted-foreground mb-2">Projection</h4>
                        <p className="text-sm text-brand-dark dark:text-muted-foreground">
                          Projections will be available once the month begins
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className={`${projectionStats.isOnTrack ? 'bg-brand-green dark:bg-brand-dark/20 border-brand-green dark:border-brand-border' : 'bg-brand-gold dark:bg-brand-dark/20 border-brand-gold dark:border-brand-border'}`}>
                      <CardContent className="pt-4">
                        <h4 className={`font-semibold mb-2 ${projectionStats.isOnTrack ? 'text-brand-dark dark:text-brand-green' : 'text-brand-dark dark:text-brand-gold'}`}>
                          {projectionStats.isPastMonth ? 'Final Result' : 'Current Projection'}
                        </h4>
                        <p className={`text-sm ${projectionStats.isOnTrack ? 'text-brand-dark dark:text-brand-green' : 'text-brand-dark dark:text-brand-gold'}`}>
                          {projectionStats.isPastMonth ? 
                            (projectionStats.isOnTrack ? 'Exceeded last month by' : 'Fell short of last month by') :
                            (projectionStats.isOnTrack ? 'Ahead of last month by' : 'Behind last month by')
                          } {formatCurrency(Math.abs(projectionStats.variance))}
                        </p>
                        <p className={`text-sm mt-1 ${projectionStats.isOnTrack ? 'text-brand-green dark:text-brand-green' : 'text-brand-gold dark:text-brand-gold'}`}>
                          ({Math.abs(projectionStats.variancePercentage).toFixed(1)}% {projectionStats.isOnTrack ? 'increase' : 'decrease'})
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Shifts Breakdown</CardTitle>
              <CardDescription>
                {groupBy === 'none' ? 'All shifts' : `Shifts grouped by ${groupBy}`} for {selectedMonth.toFormat('MMMM yyyy')}
                {otFilter !== 'all' && ` (${otFilter === 'ot' ? 'OT' : 'Non-OT'} only)`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="inline w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Group by:</span>
                <Select value={groupBy} onValueChange={(value: GroupByOption) => setGroupBy(value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">OT Filter:</span>
                <Select value={otFilter} onValueChange={(value: OTFilter) => setOtFilter(value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ot">OT Only</SelectItem>
                    <SelectItem value="no-ot">Non-OT Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {shiftsLoading ? (
            <LoadingState />
          ) : groupedShifts.length > 0 ? (
            <div className="space-y-6">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Period</TableHead>
                      <TableHead className="font-semibold text-center">Shifts</TableHead>
                      <TableHead className="font-semibold text-right">PPV</TableHead>
                      <TableHead className="font-semibold text-right">Non-PPV</TableHead>
                      <TableHead className="font-semibold text-right">Livestream</TableHead>
                      <TableHead className="font-semibold text-right">PPV Only</TableHead>
                      <TableHead className="font-semibold text-right">$/Hour</TableHead>
                      <TableHead className="font-semibold text-right">PPV Efficiency</TableHead>
                      <TableHead className="font-semibold text-right">Hours</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedGroups.map((group) => {
                      const isExpanded = expandedGroups.has(group.key);
                      
                      return (
                        <>
                          <TableRow key={group.key} className="hover:bg-muted/30 border-b">
                            <TableCell className="font-medium py-2">
                              {group.label}
                            </TableCell>
                            <TableCell className="text-center py-2">
                              <Badge variant="secondary" className="text-xs">
                                {group.shifts.length}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {formatCurrency(group.totalPPV)}
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {formatCurrency(group.totalNonPPV)}
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {formatCurrency(group.totalLivestream)}
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm font-semibold">
                              {formatCurrency(group.ppvOnly)}
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {formatCurrency(group.averagePerHour)}
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {group.totalNonPPV > 0 ? 
                                (group.totalPPV > 0 ? `${((group.ppvOnly / group.totalPPV) * 100).toFixed(1)}%` : '0%') : 
                                '-'
                              }
                            </TableCell>
                            <TableCell className="text-right py-2 font-mono text-sm">
                              {group.hoursWorked.toFixed(1)}h
                            </TableCell>
                            <TableCell className="py-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleGroupExpansion(group.key)}
                                className="h-6 w-6 p-0 hover:bg-muted"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                          
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={10} className="p-0 border-0">
                                <div className="bg-muted/20 p-3 mx-3 mb-3 rounded-lg">
                                  <div className="mb-2">
                                    <h4 className="text-sm font-semibold text-muted-foreground mb-1">
                                      Individual Shifts
                                    </h4>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <Table className="border-0">
                                      <TableHeader>
                                        <TableRow className="border-0 bg-transparent">
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 w-16">Date</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 w-20">Shift</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-16">Duration</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-20">PPV</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-20">Non-PPV</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-20">Livestream</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-20">PPV Only</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-right w-20">$/Hour</TableHead>
                                          <TableHead className="text-xs font-medium text-muted-foreground border-0 text-center w-8">OT</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {group.shifts.map((shift) => {
                                          const duration = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;
                                          const ppvOnly = (shift.ppv_earned || 0) - (shift.noppv_earned || 0);
                                          const isOT = OT_SHIFT_CODES_KEYS.includes(shift.shift_code);

                                          return (
                                            <TableRow key={shift.id} className="border-0 hover:bg-muted/30">
                                              <TableCell className="text-xs border-0 py-1">
                                                {shift.start_date_time.toFormat('MMM dd')}
                                              </TableCell>
                                              <TableCell className="border-0 py-1">
                                                <Badge 
                                                  variant="outline" 
                                                  className={`${getShiftCodeColor(shift.shift_code)} border text-xs px-1.5 py-0.5`}
                                                >
                                                  {shift.shift_code}
                                                </Badge>
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono">
                                                {duration.toFixed(1)}h
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono">
                                                {formatCurrency(shift.ppv_earned || 0)}
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono">
                                                {formatCurrency(shift.noppv_earned || 0)}
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono">
                                                {formatCurrency(shift.livestream_earned || 0)}
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono font-medium">
                                                {formatCurrency(ppvOnly)}
                                              </TableCell>
                                              <TableCell className="text-xs border-0 py-1 text-right font-mono">
                                                {duration > 0 ? formatCurrency(shift.ppv_earned / duration) : '-'}
                                              </TableCell>
                                              <TableCell className="border-0 py-1 text-center">
                                                <span className={`inline-block w-2 h-2 rounded-full ${
                                                  isOT 
                                                    ? 'bg-brand-gold' 
                                                    : 'bg-brand-dark'
                                                }`} title={isOT ? 'OT' : 'Regular'} />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, groupedShifts.length)} of {groupedShifts.length} groups
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground dark:text-muted-foreground">
                No shifts recorded for this period
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModelEarningsBreakdownView; 